import React from "react";
import {
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  alpha,
  Grow,
} from "@mui/material";
import BookIcon from "@mui/icons-material/Book";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import EditNoteIcon from "@mui/icons-material/EditNote";
import SummarizeIcon from "@mui/icons-material/Summarize";
import CallSplitIcon from "@mui/icons-material/CallSplit";
import VisibilityIcon from "@mui/icons-material/Visibility";

const ActionMenu = ({
  anchorEl,
  open,
  onClose,
  row,
  onActionClick,
  canEditDetailPL = true,
}) => {
  if (!row) return null;

  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "right",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      PaperProps={{
        sx: {
          minWidth: 220,
          boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.15)",
          borderRadius: 2,
        },
      }}
      TransitionComponent={Grow}
      transitionDuration={200}
    >
      <MenuItem
        onClick={() => onActionClick("copy")}
        sx={{
          transition: "all 0.15s ease",
          "&:hover": {
            transform: "translateX(4px)",
            backgroundColor: (theme) =>
              alpha(theme.palette.secondary.main, 0.08),
          },
        }}
      >
        <ListItemIcon>
          <ContentCopyIcon fontSize='small' color='secondary' />
        </ListItemIcon>
        <ListItemText primary='Copy Name & Amount' />
      </MenuItem>

      {canEditDetailPL && (
        <MenuItem
          onClick={() => onActionClick("delete")}
          sx={{
            transition: "all 0.15s ease",
            "&:hover": {
              transform: "translateX(4px)",
              backgroundColor: (theme) => alpha(theme.palette.error.main, 0.08),
            },
          }}
        >
          <ListItemIcon>
            <DeleteIcon fontSize='small' color='error' />
          </ListItemIcon>
          <ListItemText primary='Remove Row' />
        </MenuItem>
      )}

      {/* Schedule Actions Group */}
      {((!["", "Added Back", "Deduct"].includes(row.taxAdjustment) &&
        !row.schedule) ||
        (!["", "Added Back", "Deduct"].includes(row.taxAdjustment) &&
          row.schedule) ||
        row.schedule) && (
        <>
          {canEditDetailPL && (
            <MenuItem
              sx={{
                backgroundColor: (theme) =>
                  alpha(theme.palette.primary.main, 0.05),
                cursor: "default",
                transition: "all 0.15s ease",
                "&:hover": {
                  backgroundColor: (theme) =>
                    alpha(theme.palette.primary.main, 0.05),
                },
              }}
            >
              <ListItemText
                primary='Schedule'
                primaryTypographyProps={{
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  color: "primary.main",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              />
            </MenuItem>
          )}

          {/* Tag Schedule - Only when can edit */}
          {canEditDetailPL &&
            !["", "Added Back", "Deduct"].includes(row.taxAdjustment) &&
            !row.schedule && (
              <MenuItem
                onClick={() => onActionClick("tag")}
                sx={{
                  transition: "all 0.15s ease",
                  "&:hover": {
                    transform: "translateX(4px)",
                    backgroundColor: (theme) =>
                      alpha(theme.palette.primary.main, 0.08),
                  },
                }}
              >
                <ListItemIcon>
                  <BookIcon fontSize='small' color='primary' />
                </ListItemIcon>
                <ListItemText primary='Tag Schedule' />
              </MenuItem>
            )}

          {/* View Schedule - Always available */}
          {!["", "Added Back", "Deduct"].includes(row.taxAdjustment) &&
            row.schedule && (
              <MenuItem
                onClick={() => onActionClick("view")}
                sx={{
                  transition: "all 0.15s ease",
                  "&:hover": {
                    transform: "translateX(4px)",
                    backgroundColor: (theme) =>
                      alpha(theme.palette.primary.main, 0.08),
                  },
                }}
              >
                <ListItemIcon>
                  <VisibilityIcon fontSize='small' color='primary' />
                </ListItemIcon>
                <ListItemText primary='View Schedule' />
              </MenuItem>
            )}

          {/* Edit Schedule - Only when can edit */}
          {canEditDetailPL && row.schedule && (
            <MenuItem
              onClick={() => onActionClick("edit")}
              sx={{
                transition: "all 0.15s ease",
                "&:hover": {
                  transform: "translateX(4px)",
                  backgroundColor: (theme) =>
                    alpha(theme.palette.warning.main, 0.08),
                },
              }}
            >
              <ListItemIcon>
                <EditNoteIcon fontSize='small' color='warning' />
              </ListItemIcon>
              <ListItemText primary='Edit Schedule' />
            </MenuItem>
          )}
        </>
      )}

      {/* Merge Actions Group - Only when can edit */}
      {canEditDetailPL && row.history && row.history.length > 0 && (
        <>
          <MenuItem
            sx={{
              backgroundColor: (theme) => alpha(theme.palette.info.main, 0.05),
              cursor: "default",
              transition: "all 0.15s ease",
              "&:hover": {
                backgroundColor: (theme) =>
                  alpha(theme.palette.info.main, 0.05),
              },
            }}
          >
            <ListItemText
              primary='Merge'
              primaryTypographyProps={{
                fontSize: "0.75rem",
                fontWeight: 600,
                color: "info.main",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            />
          </MenuItem>

          {/* View Merged Summary */}
          <MenuItem
            onClick={() => onActionClick("summary")}
            sx={{
              transition: "all 0.15s ease",
              "&:hover": {
                transform: "translateX(4px)",
                backgroundColor: (theme) =>
                  alpha(theme.palette.info.main, 0.08),
              },
            }}
          >
            <ListItemIcon>
              <SummarizeIcon fontSize='small' color='info' />
            </ListItemIcon>
            <ListItemText primary='Merged Summary' />
          </MenuItem>

          {/* Unmerge */}
          {row.history.length > 1 && (
            <MenuItem
              onClick={() => onActionClick("unmerge")}
              disabled={row.taxAdjustment !== ""}
              sx={{
                transition: "all 0.15s ease",
                "&:hover": {
                  transform:
                    row.taxAdjustment === "" ? "translateX(4px)" : "none",
                  backgroundColor: (theme) =>
                    row.taxAdjustment === ""
                      ? alpha(theme.palette.info.main, 0.08)
                      : alpha(theme.palette.action.disabledBackground, 0.5),
                },
              }}
            >
              <ListItemIcon>
                <CallSplitIcon fontSize='small' color='info' />
              </ListItemIcon>
              <ListItemText
                primary='Unmerge Row'
                secondary={
                  row.taxAdjustment !== "" ? "Cannot unmerge tagged rows" : ""
                }
              />
            </MenuItem>
          )}
        </>
      )}
    </Menu>
  );
};

export default ActionMenu;
